import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RequestsByEndpoint,
  RequestsByStatus,
  RequestsOverTime,
  VizStyle,
  type NamedCount,
  type StatusCount,
  type TimePoint,
} from "./charts";

/**
 * The Usage page — "who is calling what".
 *
 * Every number here comes from Application Insights, which tags each request with the
 * **Client Key** and nothing else useful. So a client is identified by its key, an
 * endpoint by the request row's own name, and there is no join to a client id — which is
 * why the filters below are keys and names rather than ids.
 *
 * Retention is 90 days and that is a hard edge, not a default: data older than that does
 * not exist to query, so the range control says so rather than silently returning less
 * than was asked for.
 */

const RANGES = [
  { days: 1, label: "24 hours" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const STATUS_CLASSES = ["2xx", "4xx", "5xx"] as const;

export default function B2bUsage() {
  const [params, setParams] = useSearchParams();

  const days = Number(params.get("days") ?? 7);
  const clients = params.get("client") ?? "";
  const endpoints = params.get("endpoint") ?? "";
  const status = params.get("status") ?? "";

  const filter = useMemo(
    () => ({ clients, endpoints, days, status }),
    [clients, endpoints, days, status]
  );

  const config = useQuery({ queryKey: ["b2b", "usage", "config"], queryFn: api.b2b.usageConfig });
  const enabled = config.data?.configured === true;

  const timeseries = useQuery({
    queryKey: ["b2b", "usage", "timeseries", filter],
    queryFn: () => api.b2b.usage("timeseries", filter),
    enabled,
  });
  const byEndpoint = useQuery({
    queryKey: ["b2b", "usage", "by-endpoint", filter],
    queryFn: () => api.b2b.usage("by-endpoint", filter),
    enabled,
  });
  const byStatus = useQuery({
    queryKey: ["b2b", "usage", "by-status", filter],
    queryFn: () => api.b2b.usage("by-status", filter),
    enabled,
  });
  const rows = useQuery({
    queryKey: ["b2b", "usage", "rows", filter],
    queryFn: () => api.b2b.usage("rows", { ...filter, limit: 200 }),
    enabled,
  });

  const clientList = useQuery({
    queryKey: ["b2b", "clients", "for-usage"],
    queryFn: () => api.b2b.listClients({ pageSize: 200 }),
  });
  const catalog = useQuery({ queryKey: ["b2b", "catalog"], queryFn: api.b2b.catalog });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  return (
    <div className="space-y-6">
      <VizStyle />

      <div>
        <h1 className="text-2xl font-bold">Usage</h1>
        <p className="text-sm text-muted-foreground">
          Requests by client and endpoint, from Application Insights
        </p>
      </div>

      {config.data && !enabled && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Application Insights is not configured in this environment, so there are no usage
            numbers to show. The <code>B2B_AI_*</code> settings are missing on this deployment.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={clients}
          onChange={(event) => setParam("client", event.target.value)}
        >
          <option value="">All clients</option>
          {(clientList.data?.items ?? []).map((client) => (
            <option key={client.id} value={client.key}>
              {client.key}
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={endpoints}
          onChange={(event) => setParam("endpoint", event.target.value)}
        >
          <option value="">All endpoints</option>
          {(catalog.data ?? []).map((endpoint) => (
            <option key={endpoint.featureUrlId} value={endpoint.featureUrl}>
              {endpoint.docTitle ?? endpoint.featureUrl}
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={status}
          onChange={(event) => setParam("status", event.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_CLASSES.map((statusClass) => (
            <option key={statusClass} value={statusClass}>
              {statusClass}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          {RANGES.map((range) => (
            <Button
              key={range.days}
              size="sm"
              variant={days === range.days ? "secondary" : "ghost"}
              onClick={() => setParam("days", String(range.days))}
            >
              {range.label}
            </Button>
          ))}
        </div>

        {/* The cap is a property of the data, not a policy: nothing older exists. */}
        <span className="text-xs text-muted-foreground">
          90 days is the whole of Application Insights retention — there is nothing older to
          query.
        </span>
      </div>

      <Panel
        title="Requests over time"
        query={timeseries}
        render={(data) => <RequestsOverTime points={data.map(toTimePoint)} />}
      />
      <Panel
        title="By endpoint"
        query={byEndpoint}
        render={(data) => <RequestsByEndpoint rows={data.map(toNamedCount)} />}
      />
      <Panel
        title="By status"
        query={byStatus}
        render={(data) => <RequestsByStatus rows={data.map(toStatusCount)} />}
      />

      <section className="rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="font-semibold">Raw requests</h2>
          {rows.data && <Badge variant="outline">cache {rows.data.cache}</Badge>}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rows.data?.rows ?? []).map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {String(row.timestamp ?? "").slice(0, 19).replace("T", " ")}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{String(row.apiKey ?? "—")}</TableCell>
                  <TableCell className="font-mono text-xs">{String(row.name ?? "—")}</TableCell>
                  <TableCell className="text-xs">{String(row.resultCode ?? "—")}</TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {typeof row.duration === "number" ? `${Math.round(row.duration)} ms` : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{String(row.ip ?? "—")}</TableCell>
                </TableRow>
              ))}
              {rows.data && rows.data.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    No requests matched these filters in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

// Application Insights rows are genuinely untyped — the shape depends on the query text.
// Narrowing them here, once, is honest; casting would just move the risk into the charts.

function num(value: unknown): number {
  return typeof value === "number" ? value : Number(value ?? 0) || 0;
}
function str(value: unknown): string {
  return typeof value === "string" ? value : String(value ?? "");
}

function toTimePoint(row: Record<string, unknown>): TimePoint {
  return { timestamp: str(row.timestamp), requests: num(row.requests) };
}
function toNamedCount(row: Record<string, unknown>): NamedCount {
  return { name: str(row.name), requests: num(row.requests), failed: num(row.failed) };
}
function toStatusCount(row: Record<string, unknown>): StatusCount {
  return { resultCode: str(row.resultCode), requests: num(row.requests) };
}

/** One chart panel with its own loading, empty and error states. */
function Panel({
  title,
  query,
  render,
}: {
  title: string;
  query: { data?: { rows: Record<string, unknown>[]; cache: string }; isLoading: boolean; isError: boolean; error?: unknown };
  render: (rows: Record<string, unknown>[]) => React.ReactNode;
}) {
  return (
    <section className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-6 py-4">
        <h2 className="font-semibold">{title}</h2>
        {query.data && <Badge variant="outline">cache {query.data.cache}</Badge>}
      </div>
      <div className="px-6 py-4">
        {query.isLoading && <p className="py-8 text-sm text-muted-foreground">Loading…</p>}
        {query.isError && (
          <p className="py-8 text-sm text-destructive">
            {query.error instanceof Error ? query.error.message : "This query failed."}
          </p>
        )}
        {query.data && render(query.data.rows)}
      </div>
    </section>
  );
}
